/* WP-10 navigation shells (§14.6): bottom text tabs on phone, top nav on
 * tablet/desktop. Focused flows hide the tabs. */
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollectIcon, HistoryIcon, HomeIcon, HumidorIcon, MeIcon } from '../icons';
import { SessionExpiryWarning } from '../auth/session';
import { useSession } from '../auth/session-context';
import { cn } from '../utils/cn';

const TABS = [
  { to: '/', key: 'nav.home', icon: HomeIcon, end: true },
  { to: '/humidor', key: 'nav.collection', icon: HumidorIcon, end: false },
  { to: '/collect', key: 'nav.collect', icon: CollectIcon, end: false },
  { to: '/history', key: 'nav.history', icon: HistoryIcon, end: false },
  { to: '/me', key: 'nav.me', icon: MeIcon, end: false },
] as const;

/** Routes that hide the bottom tabs (focused flows). */
const FOCUSED_PREFIXES = ['/collect/new'];

export function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const { customer } = useSession();
  const focused = FOCUSED_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <div className={cn('hp-page', focused && 'hp-page--focused')}>
      <header className="hp-topnav">
        <NavLink to="/" className="hp-topnav__brand">
          <span className="hp-topnav__wordmark">
            {t('common.appName')}
            <small>{t('common.appWordmark')}</small>
          </span>
        </NavLink>
        <nav className="hp-topnav__links" aria-label={t('nav.home')}>
          {TABS.map(({ to, key, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon size={18} />
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/me" className="hp-topnav__avatar" aria-label={t('nav.me')}>
          {monogram(customer?.displayName)}
        </NavLink>
      </header>

      <main className="hp-main">
        <Outlet />
      </main>

      {!focused ? (
        <nav className="hp-tabs" aria-label={t('nav.home')}>
          {TABS.map(({ to, key, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon size={20} />
              <span className="hp-tabs__label">{t(key)}</span>
            </NavLink>
          ))}
        </nav>
      ) : null}

      <SessionExpiryWarning />
    </div>
  );
}

function monogram(name: string | null | undefined): string {
  if (!name) return '·';
  const clean = name.trim();
  if (!clean) return '·';
  const parts = clean.split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
